import { Signal } from '@angular/core';
import {
  OrganizationContact,
  OrganizationContactCallBacks,
  OrganizationContactData,
  OrganizationtContactDialogAdditionalData,
  OrganizationtContactDialogData,
  OrganizationtContactDialogMode,
} from '../types';
import { DialogSize, WcgMenuOption } from '@wcg/frontend-framework';

export interface OrganizationLookupState {
  organizationNameById: Record<string, string | null>;
  organizationWcgIdById: Record<string, string | null>;
  organizationIrbAffiliatedById: Record<string, boolean | null>;
}

export function createPendingOrganizationLookupState(
  organizationIds: string[],
  currentState: OrganizationLookupState,
): OrganizationLookupState {
  const uniqueOrganizationIds = [...new Set(organizationIds.filter(Boolean))];
  const missingIds = uniqueOrganizationIds.filter(
    (id) => !(id in currentState.organizationNameById),
  );

  if (missingIds.length === 0) {
    return {
      organizationNameById: { ...currentState.organizationNameById },
      organizationWcgIdById: { ...currentState.organizationWcgIdById },
      organizationIrbAffiliatedById: {
        ...currentState.organizationIrbAffiliatedById,
      },
    };
  }

  const pendingEntries = Object.fromEntries(missingIds.map((id) => [id, null]));

  return {
    organizationNameById: {
      ...currentState.organizationNameById,
      ...(pendingEntries as Record<string, string | null>),
    },
    organizationWcgIdById: {
      ...currentState.organizationWcgIdById,
      ...(pendingEntries as Record<string, string | null>),
    },
    organizationIrbAffiliatedById: {
      ...currentState.organizationIrbAffiliatedById,
      ...(pendingEntries as Record<string, boolean | null>),
    },
  };
}

export function buildOrganizationLookupStateFromOrganizations(
  organizations: Array<Record<string, unknown>>,
  pendingEntries: Record<string, null>,
): OrganizationLookupState {
  const organizationNameById = {
    ...pendingEntries,
  } as Record<string, string | null>;
  const organizationWcgIdById = {
    ...pendingEntries,
  } as Record<string, string | null>;
  const organizationIrbAffiliatedById = {
    ...pendingEntries,
  } as Record<string, boolean | null>;

  organizations.forEach((organization) => {
    const organizationKey =
      typeof organization.organization_key === 'string'
        ? organization.organization_key
        : '';

    if (!organizationKey) {
      return;
    }

    organizationNameById[organizationKey] =
      typeof organization.organization_name === 'string'
        ? organization.organization_name
        : null;

    organizationWcgIdById[organizationKey] =
      typeof organization.organization_id === 'string'
        ? organization.organization_id
        : null;

    organizationIrbAffiliatedById[organizationKey] =
      typeof organization.is_irb_affiliated === 'boolean'
        ? organization.is_irb_affiliated
        : null;
  });

  return {
    organizationNameById,
    organizationWcgIdById,
    organizationIrbAffiliatedById,
  };
}

export const createOrganizationContactDialogData = (
  config: {
    mode: OrganizationtContactDialogMode;
    organizationRoles: WcgMenuOption[];
    $isRequestInProgress: Signal<boolean>;
    $canCloseDialog: Signal<boolean>;
    callBacks: OrganizationContactCallBacks;
  },
  extras: OrganizationtContactDialogAdditionalData = {},
): OrganizationtContactDialogData => ({
  mode: config.mode,
  dialogTitle: getDialogTitle(config.mode),
  dialogSize: DialogSize.MEDIUM,
  organizationRoles: config.organizationRoles,
  $isRequestInProgress: config.$isRequestInProgress,
  $canCloseDialog: config.$canCloseDialog,
  callBacks: config.callBacks,
  ...extras,
});
const getDialogTitle = (mode: OrganizationtContactDialogMode): string => {
  switch (mode) {
    case 'create':
      return 'Add Organization';
    case 'edit':
      return 'Change Organization';
    case 'delete':
      return 'Remove Organization';
  }
};

export function normalizeRole(value?: string): string {
  return (value ?? '')
    .toLowerCase()
    .replace(/\s+of$/, '')
    .trim();
}

export function isRoleMatch(
  contact: Record<string, unknown>,
  role: string,
): boolean {
  const normalizedRole = normalizeRole(role);

  const orgRole = normalizeRole(
    contact.organization_role as string | undefined,
  );

  const label = normalizeRole(contact.label as string | undefined);

  return orgRole === normalizedRole || label === normalizedRole;
}

export function toNonEmptyString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim().length > 0
    ? value.trim()
    : undefined;
}

export function getRoleKey(label: WcgMenuOption): string {
  const roleValue =
    toNonEmptyString(label.value) ?? toNonEmptyString(label.label) ?? '';

  return normalizeRole(roleValue);
}

export function getRoleTitle(label: WcgMenuOption): string {
  return (
    toNonEmptyString(label.label) ??
    toNonEmptyString(label.value) ??
    'Organization Role'
  );
}

export function buildRoleSections(
  contacts: OrganizationContact[],
  labels: WcgMenuOption[],
) {
  const sections: Array<{
    key: string;
    title: string;
    contacts: OrganizationContact[];
  }> = [];

  const seen = new Set<string>();

  for (const label of labels) {
    const key = getRoleKey(label);

    if (!key || seen.has(key)) continue;
    seen.add(key);

    sections.push({
      key,
      title: getRoleTitle(label),
      contacts: contacts.filter((c) => isRoleMatch(c, key)),
    });
  }

  return sections;
}

export function extractOrganizationIds(
  contacts: OrganizationContact[],
): string[] {
  return [
    ...new Set(
      contacts
        .filter((c) => !!c.organization_id)
        .map((c) => c.organization_id as string),
    ),
  ];
}

export function enrichOrganizationFields(
  organizationId: string,
  organizationNameById: Record<string, string | null>,
  organizationWcgIdById: Record<string, string | null>,
  organizationIrbAffiliatedById: Record<string, boolean | null>,
) {
  return {
    organization_name: organizationId
      ? (organizationNameById[organizationId] ?? null)
      : null,
    is_irb_affiliated: organizationId
      ? (organizationIrbAffiliatedById[organizationId] ?? null)
      : null,
    organization_wcg_id: organizationId
      ? (organizationWcgIdById[organizationId] ?? null)
      : null,
  };
}

export function enrichContactWithOrganizationName(
  contact: OrganizationContact,
  organizationNameById: Record<string, string | null>,
  organizationWcgIdById: Record<string, string | null>,
  organizationIrbAffiliatedById: Record<string, boolean | null>,
): OrganizationContact {
  const organizationId =
    typeof contact.organization_id === 'string' ? contact.organization_id : '';

  return {
    ...contact,
    ...enrichOrganizationFields(
      organizationId,
      organizationNameById,
      organizationWcgIdById,
      organizationIrbAffiliatedById,
    ),
  };
}

export function enrichContactsWithOrganizationName(
  contacts: OrganizationContact[],
  organizationNameById: Record<string, string | null>,
  organizationWcgIdById: Record<string, string | null>,
  organizationIrbAffiliatedById: Record<string, boolean | null>,
): OrganizationContact[] {
  return contacts.map((contact) =>
    enrichContactWithOrganizationName(
      contact,
      organizationNameById,
      organizationWcgIdById,
      organizationIrbAffiliatedById,
    ),
  );
}

export function toMutationContactEntity(
  data: OrganizationContact,
  payload: OrganizationContactData,
  fallbackId: string,
): OrganizationContact {
  const organizationId =
    typeof data.organization_id === 'string' ? data.organization_id : '';

  const role =
    typeof data.organization_role === 'string'
      ? data.organization_role
      : typeof data.label === 'string'
        ? data.label
        : '';

  const relationshipId =
    typeof data.relationship_id === 'string' ? data.relationship_id : '';

  const resolvedId =
    typeof data.id === 'string' && data.id.length > 0
      ? data.id
      : relationshipId || fallbackId;

  return {
    ...data,
    id: resolvedId,
    organization_id: organizationId || payload.organization_id,
    organization_role: role || payload.organization_role.value,
    relationship_id: relationshipId || resolvedId,
  };
}
